import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Upload, Search, Trash2, UserCheck, UserX, Tag, Folder, MessageCircle, Download, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Flame, Send, PhoneOff, MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";
import { CopyContactsDialog } from "@/components/CopyContactsDialog";
import { ImportWizardL3 } from "@/components/import/ImportWizardL3";
import { CustomFieldsDialog } from "@/components/contacts/CustomFieldsDialog";
import { CustomColumnsPicker } from "@/components/contacts/CustomColumnsPicker";
import { CustomFieldsSection } from "@/components/contacts/CustomFieldsSection";
import { formatCfValue, readCols, writeCols } from "@/components/contacts/cfUtils";
import { DuplicateContactNotice } from "@/components/DuplicateContactNotice";
import { ContactStatusHistory } from "@/components/ContactStatusHistory";
import { ConsentPanel } from "@/components/ConsentPanel";
import { CreateListFromSelectionDialog } from "@/components/lists/CreateListFromSelectionDialog";
import { PreferredChannelBulkDialog } from "@/components/contacts/PreferredChannelBulkDialog";
import { ReactivationDialog } from "@/components/contacts/ReactivationDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const fmtDate = (iso, withTime = true) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT", withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" });
};


export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [prefDialog, setPrefDialog] = useState(null);
  const [reactivateId, setReactivateId] = useState(null);
  const [listDialog, setListDialog] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCluster, setFilterCluster] = useState("all");
  const [filterStore, setFilterStore] = useState("all");
  const [cfKeys, setCfKeys] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [cfCols, setCfColsState] = useState(readCols);
  const setCfCols = (keys) => { writeCols(keys); setCfColsState(keys); };
  const [cfKey, setCfKey] = useState("all");
  const [cfValues, setCfValues] = useState([]);
  const [cfValue, setCfValue] = useState("all");
  const [waNoConsent, setWaNoConsent] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [waNoConsentCount, setWaNoConsentCount] = useState(0);
  const [createdDays, setCreatedDays] = useState("all");
  const [clickedDays, setClickedDays] = useState("all");
  const [hotOnly, setHotOnly] = useState(false);
  const [undelivOnly, setUndelivOnly] = useState(() => new URLSearchParams(window.location.search).get("undeliverable") === "1");
  const [filterTag, setFilterTag] = useState(() => new URLSearchParams(window.location.search).get("tag") || "all");
  const [tagStats, setTagStats] = useState([]);
  const [sweeping, setSweeping] = useState(false);
  const { user } = useAuth();
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitPreview, setSplitPreview] = useState(null);
  const previewSplit = async () => {
    setSplitBusy(true);
    try { const { data } = await api.post("/contacts/split-surnames"); setSplitPreview(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anteprima non disponibile"); } finally { setSplitBusy(false); }
  };
  const applySplit = async () => {
    setSplitBusy(true);
    try { const { data } = await api.post("/contacts/split-surnames?apply=true"); toast.success(`${data.count} contatti aggiornati con il cognome`); setSplitPreview(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); } finally { setSplitBusy(false); }
  };
  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/contacts/undeliverable-sweep");
      toast.success(data.flagged ? `${data.flagged} numeri disattivati (totale non raggiungibili: ${data.total_undeliverable})` : `Nessun nuovo numero da disattivare (totale non raggiungibili: ${data.total_undeliverable})`);
      load();
    } catch { toast.error("Pulizia rubrica non riuscita"); } finally { setSweeping(false); }
  };
  const [sort, setSort] = useState({ field: "last_chat_at", dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    const oid = new URLSearchParams(window.location.search).get("open");
    if (!oid) return;
    api.get(`/contacts/${oid}/card`).then((r) => setEditing(r.data)).catch(() => toast.error("Contatto non trovato"));
  }, []);

  const toggleSort = (field) => setSort((s) => (s.field === field ? { field, dir: s.dir === "desc" ? "asc" : "desc" } : { field, dir: "desc" }));
  const SortIcon = ({ field }) => (sort.field !== field ? <ArrowUpDown className="h-3 w-3 inline ml-1 opacity-50" /> : sort.dir === "desc" ? <ArrowDown className="h-3 w-3 inline ml-1 text-[#10B981]" /> : <ArrowUp className="h-3 w-3 inline ml-1 text-[#10B981]" />);

  const buildParams = () => {
    const params = { sort: `${sort.field}:${sort.dir}` };
    if (search) params.search = search;
    if (filterCluster !== "all") params.cluster_id = filterCluster;
    if (filterStore !== "all") params.store = filterStore;
    if (cfKey !== "all") { params.cf_key = cfKey; if (cfValue !== "all") params.cf_value = cfValue; }
    if (waNoConsent) { params.source = "whatsapp_inbound"; params.status = "unsubscribed"; }
    else if (filterStatus !== "all") params.status = filterStatus;
    if (createdDays !== "all") params.created_days = Number(createdDays);
    if (clickedDays !== "all") params.clicked_days = Number(clickedDays);
    if (hotOnly) params.hot_only = true;
    if (undelivOnly) params.undeliverable = true;
    if (filterTag !== "all") params.tag = filterTag;
    return params;
  };
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/contacts", { params: { ...buildParams(), format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rubrica_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${contacts.length} contatti esportati (filtri e ordinamento correnti)`);
    } catch (e) { toast.error("Esportazione non riuscita"); } finally { setExporting(false); }
  };

  const load = async () => {
    const params = buildParams();
    const [c, cl, st, cf, wa, tg, fd] = await Promise.all([
      api.get("/contacts", { params }),
      api.get("/clusters"),
      api.get("/contacts/stores"),
      api.get("/contacts/custom-fields"),
      api.get("/contacts", { params: { source: "whatsapp_inbound", status: "unsubscribed" } }),
      api.get("/contacts/tag-stats", { params: { days: 30 } }).catch(() => ({ data: { tags: [] } })),
      api.get("/contacts/field-defs").catch(() => ({ data: { defs: [] } })),
    ]);
    setContacts(c.data);
    setClusters(cl.data);
    setStores(st.data);
    setCfKeys(cf.data || []);
    setFieldDefs(fd.data?.defs || []);
    setWaNoConsentCount((wa.data || []).length);
    setTagStats(tg.data?.tags || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [search, filterCluster, filterStore, cfKey, cfValue, waNoConsent, filterStatus, createdDays, clickedDays, hotOnly, undelivOnly, filterTag, sort]);

  useEffect(() => {
    setCfValue("all");
    if (cfKey === "all") { setCfValues([]); return; }
    api.get(`/contacts/custom-field-values/${encodeURIComponent(cfKey)}`).then((r) => setCfValues(r.data || [])).catch(() => setCfValues([]));
  }, [cfKey]);

  const clusterById = Object.fromEntries(clusters.map((c) => [c.id, c]));
  const cfColDefs = cfCols.map((k) => fieldDefs.find((d) => d.field_key === k && d.active)).filter(Boolean);

  const toggleConsent = async (id, status) => {
    if (status !== "subscribed") { setReactivateId(id); return; }  // H0: riattivazione solo con motivo/provenienza/evidenza
    try {
      await api.post(`/contacts/${id}/toggle-consent`);
      toast.success("Consenso aggiornato");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Operazione non consentita"); }
    load();
  };

  const remove = async (id) => {
    await api.delete(`/contacts/${id}`);
    toast.success("Contatto eliminato");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Rubrica & Cluster</h1>
          <p className="text-slate-400 text-sm mt-1">Gestisci contatti e segmenti di clienti</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} disabled={exporting || loading} data-testid="contacts-export-button"
                  title="Scarica in CSV i contatti filtrati e ordinati come a schermo"
                  className="bg-[#1E293B] border-[#38BDF8]/20 text-[#38BDF8] hover:bg-[#334155]">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Esporta CSV{!loading && contacts.length ? ` (${contacts.length})` : ""}
          </Button>
          <CustomFieldsDialog onChanged={load} />
          <CustomColumnsPicker defs={fieldDefs} selected={cfCols} onChange={setCfCols} />
          <ImportWizardL3 clusters={clusters} onDone={load} />
          <CopyContactsDialog clusters={clusters} onDone={load} />
          <ClusterDialog clusters={clusters} onDone={load} />
          <ContactDialog clusters={clusters} onDone={load} />
        </div>
      </div>

      {/* Clusters strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {clusters.map((c) => (
          <div key={c.id} className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-4 flex items-center gap-3"
               data-testid={`cluster-card-${c.id}`}>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${c.color}22` }}>
              <Folder className="h-5 w-5" style={{ color: c.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{c.name}</p>
              <p className="text-xs text-slate-500">{c.contact_count} contatti</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        {waNoConsentCount > 0 && (
          <button type="button" onClick={() => setWaNoConsent((v) => !v)} data-testid="wa-no-consent-filter"
                  className={`w-full sm:w-auto flex items-center gap-2 rounded-xl border px-3 py-2 text-xs transition-colors ${waNoConsent ? "bg-[#F59E0B]/20 border-[#F59E0B]/50 text-[#F59E0B]" : "bg-[#F59E0B]/10 border-[#F59E0B]/25 text-[#F59E0B] hover:bg-[#F59E0B]/15"}`}>
            <MessageCircle className="h-3.5 w-3.5" />
            <span><strong>{waNoConsentCount}</strong> nuovi da WhatsApp senza consenso</span>
            <span className="text-[10px] underline ml-1">{waNoConsent ? "Mostra tutti" : "Filtra"}</span>
          </button>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="contact-search-input"
            placeholder="Cerca nome, telefono, email, città, punto vendita, codice carta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#111827] border-[#1F2937] text-slate-100"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[200px] bg-[#111827] border-[#1F2937] text-slate-100" data-testid="status-filter">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="subscribed" data-testid="status-filter-subscribed">Iscritti</SelectItem>
            <SelectItem value="pending_consent" data-testid="status-filter-pending">Consenso da acquisire</SelectItem>
            <SelectItem value="unsubscribed" data-testid="status-filter-unsubscribed">Disiscritti</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCluster} onValueChange={setFilterCluster}>
          <SelectTrigger className="w-[200px] bg-[#111827] border-[#1F2937] text-slate-100" data-testid="cluster-filter">
            <SelectValue placeholder="Tutti i cluster" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Tutti i cluster</SelectItem>
            {clusters.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStore} onValueChange={setFilterStore}>
          <SelectTrigger className="w-[200px] bg-[#111827] border-[#1F2937] text-slate-100" data-testid="store-filter">
            <SelectValue placeholder="Tutti i punti vendita" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Tutti i punti vendita</SelectItem>
            {stores.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTag} onValueChange={setFilterTag}>
          <SelectTrigger className="w-[190px] bg-[#111827] border-[#1F2937] text-slate-100" data-testid="tag-filter">
            <SelectValue placeholder="Tutti i tag" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Tutti i tag</SelectItem>
            {tagStats.map((t) => (
              <SelectItem key={t.tag} value={t.tag}>{t.tag} ({t.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={createdDays} onValueChange={setCreatedDays}>
          <SelectTrigger className="w-[190px] bg-[#111827] border-[#1F2937] text-slate-100" data-testid="created-days-filter">
            <SelectValue placeholder="Aggiunti quando" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Aggiunti in qualsiasi data</SelectItem>
            <SelectItem value="7">Ultimi 7 giorni</SelectItem>
            <SelectItem value="30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ultimi 90 giorni</SelectItem>
          </SelectContent>
        </Select>
        <Select value={clickedDays} onValueChange={setClickedDays}>
          <SelectTrigger className={`w-[210px] bg-[#111827] text-slate-100 ${clickedDays !== "all" ? "border-[#F59E0B]/60" : "border-[#1F2937]"}`} data-testid="clicked-days-filter">
            <SelectValue placeholder="Ha cliccato un link" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
            <SelectItem value="all">Clic sui link: qualsiasi</SelectItem>
            <SelectItem value="7">Ha cliccato negli ultimi 7 giorni</SelectItem>
            <SelectItem value="30">Ha cliccato negli ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ha cliccato negli ultimi 90 giorni</SelectItem>
          </SelectContent>
        </Select>
        <button type="button" onClick={() => { setHotOnly(!hotOnly); if (!hotOnly) setSort({ field: "clicks_90d", dir: "desc" }); }} data-testid="hot-clients-filter"
                className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-sm transition-colors ${hotOnly ? "bg-[#F97316]/15 border-[#F97316] text-[#FDBA74]" : "bg-[#111827] border-[#1F2937] text-slate-300 hover:text-slate-100"}`}>
          <Flame className="h-4 w-4" /> Clienti caldi
        </button>
        <button type="button" onClick={() => setUndelivOnly(!undelivOnly)} data-testid="undeliverable-filter"
                className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-sm transition-colors ${undelivOnly ? "bg-[#EF4444]/15 border-[#EF4444] text-[#FCA5A5]" : "bg-[#111827] border-[#1F2937] text-slate-300 hover:text-slate-100"}`}
                title="Numeri disattivati perché la consegna è fallita in 2 campagne di fila">
          <PhoneOff className="h-4 w-4" /> Non raggiungibili
        </button>
        <button type="button" onClick={runSweep} disabled={sweeping} data-testid="undeliverable-sweep-button"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#1F2937] bg-[#111827] text-sm text-slate-300 hover:text-slate-100 transition-colors disabled:opacity-50"
                title="Controlla ora e disattiva chi ha fallito la consegna in 2 campagne consecutive">
          {sweeping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Pulizia rubrica
        </button>
        {user?.role === "admin" && (
          <button type="button" onClick={previewSplit} disabled={splitBusy} data-testid="split-surnames-button"
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#1F2937] bg-[#111827] text-sm text-slate-300 hover:text-slate-100 transition-colors disabled:opacity-50"
                  title="Divide i contatti 'Nome Cognome' nel nuovo campo Cognome">
            {splitBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Separa cognomi
          </button>
        )}
        <button type="button" onClick={async () => { try { const { data } = await api.post("/contacts/hot-clients-list"); navigate(`/campagne?list=${data.id}&listName=${encodeURIComponent(data.name)}&count=${data.contact_count}&name=${encodeURIComponent("Campagna Clienti caldi")}`); } catch { toast.error("Impossibile creare la lista Clienti caldi"); } }}
                data-testid="hot-clients-campaign-button" title="Crea una campagna mirata ai Clienti caldi"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md bg-[#F97316] text-[#0A0E17] text-sm font-semibold hover:bg-[#EA580C] transition-colors">
          <Send className="h-4 w-4" /> Campagna ai caldi
        </button>
        {cfKeys.length > 0 && (
          <div className="flex items-center gap-2" data-testid="custom-field-filter">
            <Select value={cfKey} onValueChange={setCfKey}>
              <SelectTrigger className="w-[190px] bg-[#111827] border-[#10B981]/30 text-slate-100" data-testid="custom-field-key-filter">
                <SelectValue placeholder="Campo personalizzato" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                <SelectItem value="all">Campo personalizzato…</SelectItem>
                {cfKeys.map((k) => <SelectItem key={k} value={k}>{fieldDefs.find((d) => d.field_key === k)?.label || k}</SelectItem>)}
              </SelectContent>
            </Select>
            {cfKey !== "all" && (
              <Select value={cfValue} onValueChange={setCfValue}>
                <SelectTrigger className="w-[170px] bg-[#111827] border-[#10B981]/30 text-slate-100" data-testid="custom-field-value-filter">
                  <SelectValue placeholder="Qualsiasi valore" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                  <SelectItem value="all">Qualsiasi valore</SelectItem>
                  {cfValues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {cfKey !== "all" && (
              <button type="button" onClick={() => setCfKey("all")} data-testid="custom-field-filter-clear"
                      className="text-xs text-slate-500 hover:text-[#EF4444]">Azzera</button>
            )}
          </div>
        )}
      </div>

      {/* Ordinamento */}
      <Dialog open={!!splitPreview} onOpenChange={(o) => { if (!o) setSplitPreview(null); }}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100" data-testid="split-surnames-dialog">
          <DialogHeader><DialogTitle>Separa cognomi</DialogTitle></DialogHeader>
          {splitPreview && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-300">
                <b data-testid="split-surnames-count">{splitPreview.count}</b> contatti hanno un nome con più parole e nessun cognome: l'ultima parola diventerà il cognome.
                {splitPreview.skipped > 0 && <span className="text-slate-500"> {splitPreview.skipped} contatti con una sola parola o solo numero restano invariati.</span>}
              </p>
              {splitPreview.examples.length > 0 && (
                <div className="rounded-lg border border-[#1F2937] divide-y divide-[#1F2937] text-xs">
                  {splitPreview.examples.map((e, i) => (
                    <div key={i} className="px-3 py-1.5 flex items-center gap-2"><span className="text-slate-500 line-through">{e.from}</span><span className="text-slate-500">→</span><span className="text-slate-200">{e.name} <b>{e.surname}</b></span></div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitPreview(null)} className="border-[#1F2937] bg-transparent text-slate-300" data-testid="split-surnames-cancel">Annulla</Button>
            <Button onClick={applySplit} disabled={splitBusy || !splitPreview?.count} className="bg-[#10B981] text-[#0A0E17] hover:bg-[#059669]" data-testid="split-surnames-apply">
              {splitBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Applica a ${splitPreview?.count ?? 0} contatti`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-wrap items-center gap-2" data-testid="contacts-sort-bar">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-medium mr-1">Ordina per</span>
        {[["last_chat_at", "Ultima chat", MessagesSquare], ["surname", "Cognome", null], ["name", "Nome", null], ["created_at", "Data creazione", null]].map(([f, label, Icon]) => (
          <button key={f} type="button" onClick={() => toggleSort(f)} data-testid={`sort-${f}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sort.field === f ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/40" : "bg-[#111827] text-slate-400 border-[#1F2937] hover:text-slate-200"}`}>
            {Icon && <Icon className="h-3.5 w-3.5" />}{label}<SortIcon field={f} />
          </button>
        ))}
      </div>

      {/* Table */}
      {tagStats.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tag-chips">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1 flex items-center gap-1"><Tag className="h-3 w-3" /> Tag</span>
          {tagStats.map((t) => (
            <button key={t.tag} type="button" onClick={() => setFilterTag(filterTag === t.tag ? "all" : t.tag)} data-testid={`tag-chip-${t.tag}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${filterTag === t.tag ? "bg-[#8B5CF6] border-[#8B5CF6] text-white" : "border-[#1F2937] text-slate-300 hover:border-[#8B5CF6]/50"}`}>
              {t.tag} <span className={filterTag === t.tag ? "opacity-90" : "text-slate-500"}>· {t.count}</span>
            </button>
          ))}
          {filterTag !== "all" && <button type="button" onClick={() => setFilterTag("all")} data-testid="tag-chip-clear" className="text-xs text-slate-400 hover:text-slate-200 underline ml-1">Togli filtro</button>}
        </div>
      )}
      <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {selected.length > 0 && (
                <tr><th colSpan={13} className="px-4 py-2 bg-[#10B981]/10 border-b border-[#10B981]/30 font-normal">
                  <div className="flex flex-wrap items-center gap-2 text-sm sticky left-0 w-fit max-w-[calc(100vw-4rem)]" data-testid="selection-bar">
                    <span className="text-slate-100"><span className="font-semibold" data-testid="selection-count">{selected.length}</span> selezionati</span>
                    <Button size="sm" onClick={() => setListDialog(true)} className="bg-[#10B981] hover:bg-[#059669] text-white h-8" data-testid="create-list-from-selection-btn">Crea lista dai selezionati</Button>
                    <Button size="sm" variant="outline" onClick={() => setPrefDialog("selection")} className="h-8 border-[#3B82F6]/50 text-[#93C5FD] hover:bg-[#3B82F6]/10" data-testid="set-preferred-channel-btn">Imposta canale preferito</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="h-8 text-slate-300" data-testid="clear-selection-btn">Deseleziona</Button>
                  </div>
                </th></tr>
              )}
              <tr className="border-b border-[#1F2937] text-slate-500 text-left">
                <th className="px-3 py-3 w-8"><input type="checkbox" aria-label="Seleziona tutti" checked={contacts.length > 0 && contacts.every((c) => selected.includes(c.id))} onChange={(e) => setSelected(e.target.checked ? Array.from(new Set([...selected, ...contacts.map((c) => c.id)])) : selected.filter((id) => !contacts.some((c) => c.id === id)))} className="accent-[#10B981] h-4 w-4" data-testid="select-all-contacts" /></th>
                <th className="px-5 py-3 font-medium">Contatto</th>
                <th className="px-5 py-3 font-medium">Telefono</th>
                <th className="px-5 py-3 font-medium hidden lg:table-cell">Punto vendita</th>
                <th className="px-5 py-3 font-medium hidden lg:table-cell">Città</th>
                <th className="px-5 py-3 font-medium hidden lg:table-cell">Codice carta</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Cluster / Tag</th>
                {cfColDefs.map((d) => <th key={d.field_key} className="px-4 py-3 font-medium hidden lg:table-cell whitespace-nowrap" title={`Campo personalizzato · ${d.field_key}`} data-testid={`cf-col-head-${d.field_key}`}>{d.label}</th>)}
                <th className="px-5 py-3 font-medium">Stato</th>
                <th className="px-4 py-3 font-medium hidden xl:table-cell whitespace-nowrap cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("created_at")} data-testid="sort-created">Creato il<SortIcon field="created_at" /></th>
                <th className="px-4 py-3 font-medium hidden xl:table-cell whitespace-nowrap cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("updated_at")} data-testid="sort-updated">Ultima modifica<SortIcon field="updated_at" /></th>
                <th className="px-4 py-3 font-medium hidden xl:table-cell whitespace-nowrap cursor-pointer select-none hover:text-slate-300" onClick={() => toggleSort("clicks_90d")} data-testid="sort-clicks">Clic 90gg<SortIcon field="clicks_90d" /></th>
                <th className="px-5 py-3 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody data-testid="contacts-table">
              {loading ? (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">Caricamento...</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">Nessun contatto trovato</td></tr>
              ) : (
                contacts.map((ct) => (
                  <tr key={ct.id} onClick={() => setEditing(ct)}
                      data-testid={`contact-row-${ct.id}`}
                      className="border-b border-[#1F2937]/60 hover:bg-[#1F2937]/30 transition-colors cursor-pointer">
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label="Seleziona contatto" checked={selected.includes(ct.id)} onChange={() => setSelected((p) => (p.includes(ct.id) ? p.filter((x) => x !== ct.id) : [...p, ct.id]))} className="accent-[#10B981] h-4 w-4" data-testid={`select-contact-${ct.id}`} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#1E293B] flex items-center justify-center text-xs font-semibold text-[#10B981]">
                          {(ct.name || ct.phone || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-slate-200 flex items-center gap-1.5 flex-wrap">{ct.name ? <>{ct.name}{ct.surname && <span className="font-semibold" data-testid={`contact-surname-${ct.id}`}> {ct.surname}</span>}</> : <span className="text-slate-500 italic">Senza nome</span>}
                            {ct.undeliverable && (
                              <Badge className="bg-[#EF4444]/15 text-[#FCA5A5] border-0 text-[10px] px-1.5 py-0 gap-0.5" title={ct.undeliverable_reason || "Consegna fallita in 2 campagne consecutive"} data-testid={`undeliverable-badge-${ct.id}`}>
                                <PhoneOff className="h-3 w-3" /> Non raggiungibile
                              </Badge>
                            )}
                            {ct.preferred_channel === "sms" && (
                              <Badge className="bg-[#3B82F6]/15 text-[#1D4ED8] border-0 text-[10px] px-1.5 py-0" title="Canale preferito: SMS" data-testid={`preferred-sms-badge-${ct.id}`}>SMS</Badge>
                            )}
                            {(ct.clicks_90d || 0) >= 2 && (
                              <Badge className="bg-[#F97316]/15 text-[#FDBA74] border-0 text-[10px] px-1.5 py-0 gap-0.5" title={`${ct.clicks_90d} clic negli ultimi 90 giorni`} data-testid={`hot-client-badge-${ct.id}`}>
                                <Flame className="h-3 w-3" /> Cliente caldo · {ct.clicks_90d}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{ct.email}</p>
                          {ct.last_chat_at && (
                            <p className="text-[11px] text-[#10B981]/80 flex items-center gap-1.5 flex-wrap" data-testid={`contact-last-chat-${ct.id}`}>
                              <MessagesSquare className="h-3 w-3" /> Ultima chat {fmtDate(ct.last_chat_at)}
                              <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/chat?phone=${encodeURIComponent(ct.phone)}`); }}
                                      data-testid={`open-chat-${ct.id}`}
                                      className="ml-1 px-1.5 py-0.5 rounded-md border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-[#0A0E17] transition-colors text-[10px] font-semibold whitespace-nowrap shrink-0">
                                Apri chat
                              </button>
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono2 text-slate-300">{ct.phone}</td>
                    <td className="px-5 py-3 hidden lg:table-cell text-slate-300">{ct.store || <span className="text-slate-600">—</span>}</td>
                    <td className="px-5 py-3 hidden lg:table-cell text-slate-300">{ct.city || <span className="text-slate-600">—</span>}</td>
                    <td className="px-5 py-3 hidden lg:table-cell font-mono2 text-slate-300">{ct.card_code || <span className="text-slate-600">—</span>}</td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(ct.cluster_ids || []).map((id) =>
                          clusterById[id] ? (
                            <Badge key={id} variant="outline" className="border-0 text-xs"
                                   style={{ backgroundColor: `${clusterById[id].color}22`, color: clusterById[id].color }}>
                              {clusterById[id].name}
                            </Badge>
                          ) : null
                        )}
                        {(ct.tags || []).map((t) => (
                          <button key={t} type="button" onClick={() => setFilterTag(t)} data-testid={`contact-tag-${ct.id}-${t}`} title="Filtra per questo tag"
                                  className="px-1.5 py-0.5 rounded text-[10px] bg-[#8B5CF6]/15 text-[#A78BFA] border border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/30">#{t}</button>
                        ))}
                      </div>
                    </td>
                    {cfColDefs.map((d) => <td key={d.field_key} className="px-4 py-3 hidden lg:table-cell text-xs text-slate-300 max-w-[180px] truncate" title={formatCfValue(d, (ct.custom_fields || {})[d.field_key])} data-testid={`cf-col-${d.field_key}-${ct.id}`}>{formatCfValue(d, (ct.custom_fields || {})[d.field_key]) || <span className="text-slate-600">—</span>}</td>)}
                    <td className="px-5 py-3">
                      {ct.status === "subscribed" ? (
                        <Badge className="bg-[#10B981]/15 text-[#10B981] border-0">Iscritto</Badge>
                      ) : ct.status === "pending_consent" ? (
                        <Badge data-testid={`contact-pending-consent-${ct.id}`} className="bg-[#F59E0B]/15 text-[#F59E0B] border-0" title="Nessun consenso WhatsApp documentato: escluso da campagne e automazioni finché non viene registrata un'autorizzazione (protezione legacy attiva fino a L4)">Consenso da acquisire</Badge>
                      ) : (
                        <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-0">Disiscritto</Badge>
                      )}
                      {(ct.whatsapp_consent || ct.sms_consent) && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`contact-consents-${ct.id}`}>
                          {[["WhatsApp", ct.whatsapp_consent], ["SMS", ct.sms_consent]].map(([lab, c]) => {
                            const v = c?.value;
                            const [txt, cls] = v === true ? ["consenso acquisito", "bg-[#10B981]/15 text-[#047857]"] : v === false ? ["consenso negato", "bg-[#EF4444]/15 text-[#B91C1C]"] : ["consenso da acquisire", "bg-[#F59E0B]/15 text-[#B45309]"];
                            return <span key={lab} className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`} title={c?.evidence?.source ? `Fonte: ${c.evidence.source}` : undefined} data-testid={`contact-consent-${lab.toLowerCase()}-${ct.id}`}>{lab}: {txt}</span>;
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-400 whitespace-nowrap" title={fmtDate(ct.created_at)} data-testid={`contact-created-${ct.id}`}>{fmtDate(ct.created_at, false)}</td>
                    <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-400 whitespace-nowrap" title={ct.updated_at ? fmtDate(ct.updated_at) : ""} data-testid={`contact-updated-${ct.id}`}>{ct.updated_at ? fmtDate(ct.updated_at, false) : <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 hidden xl:table-cell text-xs whitespace-nowrap" data-testid={`contact-clicks-${ct.id}`}>{ct.clicks_90d ? <span className="text-[#F59E0B] font-semibold">{ct.clicks_90d}</span> : <span className="text-slate-600">—</span>}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/chat?phone=${encodeURIComponent(ct.phone)}`); }}
                          data-testid={`chat-contact-${ct.id}`}
                          className="text-slate-500 hover:text-[#8B5CF6] transition-colors"
                          title="Apri chat"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleConsent(ct.id, ct.status); }}
                          data-testid={`toggle-consent-${ct.id}`}
                          className="text-slate-500 hover:text-[#10B981] transition-colors"
                          title="Cambia consenso"
                        >
                          {ct.status === "subscribed" ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                        {ct.undeliverable && (
                          <button
                            onClick={async (e) => { e.stopPropagation(); try { await api.post(`/contacts/${ct.id}/reactivate`); toast.success("Numero riattivato: tornerà a ricevere le campagne"); load(); } catch { toast.error("Riattivazione non riuscita"); } }}
                            data-testid={`reactivate-contact-${ct.id}`}
                            className="text-[#F59E0B] hover:text-[#10B981] transition-colors"
                            title="Riattiva numero (torna a ricevere le campagne)"
                          >
                            <PhoneOff className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); remove(ct.id); }}
                          data-testid={`delete-contact-${ct.id}`}
                          className="text-slate-500 hover:text-[#EF4444] transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-600">
        Formato CSV supportato: colonne <span className="font-mono2">nome, cognome, telefono, email, punto vendita, codice carta, citta</span> (o name, surname, phone, email, store, card_code, city).
      </p>

      <EditContactDialog clusters={clusters} contact={editing} fieldDefs={fieldDefs} onClose={() => setEditing(null)} onDone={load} />
      <CreateListFromSelectionDialog open={listDialog} onClose={() => setListDialog(false)} contactIds={selected} clusters={clusters} onDone={() => setSelected([])} />
      <ReactivationDialog contactId={reactivateId} onClose={() => setReactivateId(null)} onDone={load} />
      <PreferredChannelBulkDialog open={!!prefDialog} onClose={() => setPrefDialog(null)} contactIds={selected} allWithChannel={prefDialog === "all_sms" ? "sms" : null} onDone={() => { setSelected([]); load(); }} />
    </div>
  );
}

function ContactDialog({ clusters, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", phone: "", email: "", store: "", card_code: "", city: "", cluster_ids: [] });
  const [dup, setDup] = useState(null);
  const [canForce, setCanForce] = useState(false);
  useEffect(() => { if (open) api.get("/me/permissions").then((r) => setCanForce(["super_admin", "tenant_admin"].includes(r.data.role))).catch(() => {}); else setDup(null); }, [open]);

  const save = async (force) => {
    if (!form.name || !form.phone) {
      toast.error("Nome e telefono obbligatori");
      return;
    }
    try {
      await api.post("/contacts", force ? { ...form, force_duplicate: true, force_reason: force } : form);
      toast.success(force ? "Contatto creato (duplicato forzato, tracciato nel log)" : "Contatto aggiunto");
      setOpen(false);
      setDup(null);
      setForm({ name: "", surname: "", phone: "", email: "", store: "", card_code: "", city: "", cluster_ids: [] });
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "duplicate_contact") { setDup(d.existing); return; }
      toast.error(formatApiErrorDetail(d));
    }
  };

  const toggleCluster = (id) =>
    setForm((f) => ({
      ...f,
      cluster_ids: f.cluster_ids.includes(id) ? f.cluster_ids.filter((x) => x !== id) : [...f.cluster_ids, id],
    }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-contact-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
          <Plus className="h-4 w-4 mr-2" /> Nuovo contatto
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Nuovo contatto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Nome</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
            <div className="space-y-2"><Label>Cognome</Label>
              <Input data-testid="contact-surname-input" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
          </div>
          <div className="space-y-2"><Label>Telefono</Label>
            <Input data-testid="contact-phone-input" value={form.phone} placeholder="+39..." onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="space-y-2"><Label>Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Punto vendita</Label>
              <Input data-testid="contact-store-input" value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
            <div className="space-y-2"><Label>Città</Label>
              <Input data-testid="contact-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
          </div>
          <div className="space-y-2"><Label>Codice carta</Label>
            <Input data-testid="contact-card-input" value={form.card_code} onChange={(e) => setForm({ ...form, card_code: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="space-y-2"><Label>Cluster</Label>
            <div className="flex flex-wrap gap-2">
              {clusters.map((c) => (
                <button key={c.id} onClick={() => toggleCluster(c.id)} type="button"
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                          form.cluster_ids.includes(c.id)
                            ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]"
                            : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"
                        }`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          {dup && <DuplicateContactNotice existing={dup} canForce={canForce} onForce={(reason) => save(reason)} />}
        </div>
        <DialogFooter>
          <Button onClick={() => save()} data-testid="save-contact-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            Salva contatto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactEmailPanel({ contact, form }) {
  const [tpls, setTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const load = () => api.get(`/contacts/${contact.id}/emails`).then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/email-templates").then((r) => setTpls(r.data || [])).catch(() => {}); load(); /* eslint-disable-next-line */ }, [contact.id]);
  const canSend = !!form.email && !!form.email_consent && !!tplId;
  const send = async () => {
    setSending(true);
    try { await api.post(`/contacts/${contact.id}/send-email`, { template_id: tplId }); toast.success("Email inviata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); } finally { setSending(false); }
  };
  return (
    <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2 space-y-2" data-testid="contact-email-panel">
      <p className="text-sm text-slate-200">Email transazionale</p>
      {!form.email ? <p className="text-xs text-slate-500">Aggiungi un indirizzo email al contatto per poter inviare.</p>
        : !form.email_consent ? <p className="text-xs text-amber-400">Attiva e salva il consenso email per abilitare l'invio.</p>
        : tpls.length === 0 ? <p className="text-xs text-slate-500">Nessun template: creane uno nella sezione "Email transazionali".</p> : (
          <div className="flex gap-2">
            <Select value={tplId} onValueChange={setTplId}>
              <SelectTrigger className="bg-[#111827] border-[#1F2937] h-9 flex-1" data-testid="contact-email-template-select"><SelectValue placeholder="Scegli il template…" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">{tpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" size="sm" onClick={send} disabled={!canSend || sending} data-testid="contact-send-email-button" className="bg-[#1E4FD8] hover:bg-[#1a44bb] text-white h-9">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        )}
      {history.length > 0 && (
        <div className="text-[11px] text-slate-500 space-y-0.5" data-testid="contact-email-history">
          {history.slice(0, 4).map((h) => <p key={h.id}>{fmtDate(h.sent_at)} · {h.template_name} · <span className={h.status === "sent" ? "text-emerald-400" : "text-rose-400"}>{h.status === "sent" ? "inviata" : "errore"}</span></p>)}
        </div>
      )}
    </div>
  );
}

function ContactFormSubmissions({ contactId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (!contactId) return; api.get(`/contacts/${contactId}/form-submissions`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [contactId]);
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-[#1F2937] p-3 space-y-1" data-testid="contact-form-submissions">
      <p className="text-xs font-semibold text-slate-200">Form compilati ({items.length})</p>
      {items.slice(0, 5).map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-300" data-testid={`contact-form-sub-${s.id}`}>
          <span className="text-slate-500">{new Date(s.received_at).toLocaleString("it-IT")}</span>
          <a className="text-[#1E4FD8] hover:underline" href={`/form/${s.form_id}/risposte`}>{s.form_name} v{s.form_version}</a>
          <span className={s.validation_status === "validated" ? "text-green-700" : "text-red-600"}>{s.validation_status === "validated" ? "valida" : "non valida"}</span>
          <span className="text-slate-500">· CRM {s.crm_status === "updated" ? "aggiornato" : s.crm_status === "failed" ? "errore" : "solo storico"}</span>
          {s.test && <span className="px-1 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">TEST</span>}
        </div>))}
    </div>
  );
}

function EditContactDialog({ clusters, contact, fieldDefs = [], onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [newCustom, setNewCustom] = useState({ key: "", value: "" });
  const [smsStatus, setSmsStatus] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || "",
        surname: contact.surname || "",
        phone: contact.phone || "",
        email: contact.email || "",
        store: contact.store || "",
        card_code: contact.card_code || "",
        city: contact.city || "",
        cluster_ids: contact.cluster_ids || [],
        status: contact.status || "subscribed",
        custom_fields: contact.custom_fields || {},
        preferred_channel: contact.preferred_channel || "whatsapp",
        email_consent: !!contact.email_consent,
      });
      setNewCustom({ key: "", value: "" });
      setSmsStatus(null);
      api.get(`/contacts/${contact.id}/sms-status`).then((r) => setSmsStatus(r.data)).catch(() => setSmsStatus({ error: true }));
    }
  }, [contact]);

  const resetSms = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/sms-reset`);
      setSmsStatus(data);
      toast.success("Fallback SMS riabilitato per questo contatto");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Operazione non riuscita"); } finally { setResetting(false); }
  };

  const toggleCluster = (id) =>
    setForm((f) => ({
      ...f,
      cluster_ids: f.cluster_ids.includes(id) ? f.cluster_ids.filter((x) => x !== id) : [...f.cluster_ids, id],
    }));

  const save = async () => {
    if (!form.name || !form.phone) {
      toast.error("Nome e telefono obbligatori");
      return;
    }
    try {
      await api.put(`/contacts/${contact.id}`, form);
      toast.success("Scheda cliente aggiornata");
      onClose();
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setCustom = (k, v) => setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [k]: v } }));
  const removeCustom = (k) => setForm((f) => { const c = { ...f.custom_fields }; delete c[k]; return { ...f, custom_fields: c }; });
  const addCustom = () => {
    const k = newCustom.key.trim();
    if (!k) { toast.error("Inserisci il nome del campo"); return; }
    setCustom(k, newCustom.value);
    setNewCustom({ key: "", value: "" });
  };

  if (!contact || !form) return null;

  return (
    <Dialog open={!!contact} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">Scheda cliente</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-slate-500 -mt-1" data-testid="contact-dates">
          <span>Creato il: <span className="text-slate-300">{fmtDate(contact?.created_at)}</span></span>
          <span>Ultima modifica: <span className="text-slate-300">{contact?.updated_at ? fmtDate(contact.updated_at) : "mai modificato"}</span></span>
          {contact?.last_click_at && <span data-testid="contact-last-click">Ultimo clic link: <span className="text-[#F59E0B]">{fmtDate(contact.last_click_at)}</span></span>}
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Nome</Label>
              <Input data-testid="edit-contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
            <div className="space-y-2"><Label>Cognome</Label>
              <Input data-testid="edit-contact-surname-input" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
          </div>
          <div className="space-y-2"><Label>Telefono</Label>
            <Input data-testid="edit-contact-phone-input" value={form.phone} placeholder="+39..." onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="space-y-2"><Label>Email</Label>
            <Input data-testid="edit-contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Punto vendita</Label>
              <Input data-testid="edit-contact-store-input" value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
            <div className="space-y-2"><Label>Città</Label>
              <Input data-testid="edit-contact-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                     className="bg-[#0A0E17] border-[#1F2937]" /></div>
          </div>
          <div className="space-y-2"><Label>Codice carta</Label>
            <Input data-testid="edit-contact-card-input" value={form.card_code} onChange={(e) => setForm({ ...form, card_code: e.target.value })}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <CustomFieldsSection defs={fieldDefs} values={form.custom_fields} onChange={setCustom} onRemove={removeCustom} newCustom={newCustom} setNewCustom={setNewCustom} onAdd={addCustom} />
          <div className="flex items-center justify-between bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2">
            <div>
              <p className="text-sm text-slate-200">Iscritto (consenso invio)</p>
              <p className="text-xs text-slate-500">Se disattivo, il contatto non riceverà le campagne</p>
            </div>
            <Switch data-testid="edit-contact-consent-switch" checked={form.status === "subscribed"}
                    onCheckedChange={(v) => setForm({ ...form, status: v ? "subscribed" : "unsubscribed" })} />
          </div>
          <div className="flex items-center justify-between bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2">
            <div>
              <p className="text-sm text-slate-200">Consenso email</p>
              <p className="text-xs text-slate-500">Permette l'invio di email transazionali (benvenuto, conferme, promemoria)</p>
            </div>
            <Switch data-testid="edit-contact-email-consent-switch" checked={!!form.email_consent}
                    onCheckedChange={(v) => setForm({ ...form, email_consent: v })} />
          </div>
          <ContactEmailPanel contact={contact} form={form} />
          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2 space-y-2" data-testid="preferred-channel-section">
            <div>
              <p className="text-sm text-slate-200">Canale preferito</p>
              <p className="text-xs text-slate-500">Con «SMS» le campagne WhatsApp gli inviano direttamente l'SMS (se la campagna ha un testo SMS), altrimenti viene escluso.</p>
            </div>
            <div className="flex gap-2">
              {[["whatsapp", "WhatsApp"], ["sms", "SMS"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, preferred_channel: v })} data-testid={`preferred-channel-${v}`}
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${form.preferred_channel === v ? (v === "sms" ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-[#10B981] text-[#0A0E17] border-[#10B981]") : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl px-3 py-2" data-testid="sms-status-section">
            <p className="text-sm text-slate-200">Stato SMS</p>
            {!smsStatus ? <p className="text-xs text-slate-500">Caricamento…</p>
              : smsStatus.error ? <p className="text-xs text-slate-500">Stato non disponibile</p>
              : smsStatus.blocked || smsStatus.last_error ? (
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="text-xs">
                    <p className="text-[#FCA5A5]" data-testid="sms-last-error">Ultimo errore SMS: {smsStatus.last_error_label || smsStatus.last_error || "sconosciuto"}{smsStatus.last_failed_at ? ` · ${fmtDate(smsStatus.last_failed_at)}` : ""}</p>
                    <p className="text-slate-500">{smsStatus.blocked ? "Escluso dal fallback SMS per 90 giorni (per non sprecare crediti)." : "Non più escluso dal fallback SMS."}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={resetSms} disabled={resetting} data-testid="sms-reset-button"
                          className="bg-[#1E293B] border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#334155] shrink-0 h-8 text-xs">
                    {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Riabilita fallback SMS"}
                  </Button>
                </div>
              ) : <p className="text-xs text-slate-500" data-testid="sms-status-ok">Nessun errore SMS registrato{smsStatus.reset_at ? ` · fallback riabilitato il ${fmtDate(smsStatus.reset_at)}` : ""}.</p>}
          </div>
          <div className="space-y-2"><Label>Cluster</Label>
            <div className="flex flex-wrap gap-2">
              {clusters.map((c) => (
                <button key={c.id} onClick={() => toggleCluster(c.id)} type="button"
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                          form.cluster_ids.includes(c.id)
                            ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]"
                            : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"
                        }`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ConsentPanel contactId={contact?.id} />
        <ContactStatusHistory contactId={contact?.id} />
        <ContactFormSubmissions contactId={contact?.id} />
        <DialogFooter>
          <Button onClick={save} data-testid="save-edit-contact-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClusterDialog({ clusters, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#10B981");
  const colors = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899"];

  const save = async () => {
    if (!name) return;
    await api.post("/clusters", { name, color });
    toast.success("Cluster creato");
    setName("");
    setOpen(false);
    onDone();
  };

  const remove = async (id) => {
    await api.delete(`/clusters/${id}`);
    toast.success("Cluster eliminato");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-clusters-button"
                className="bg-[#1E293B] border-[#10B981]/20 text-[#10B981] hover:bg-[#334155]">
          <Tag className="h-4 w-4 mr-2" /> Cluster
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Gestione cluster</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {clusters.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-[#0A0E17] rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: c.color }} />
                <span className="text-sm text-slate-200">{c.name}</span>
                <span className="text-xs text-slate-500">({c.contact_count})</span>
              </div>
              <button onClick={() => remove(c.id)} className="text-slate-500 hover:text-[#EF4444]">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="border-t border-[#1F2937] pt-3 space-y-2">
            <Label>Nuovo cluster</Label>
            <Input data-testid="cluster-name-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Es. Clienti VIP" className="bg-[#0A0E17] border-[#1F2937]" />
            <div className="flex gap-2">
              {colors.map((col) => (
                <button key={col} onClick={() => setColor(col)} type="button"
                        className={`h-7 w-7 rounded-full transition-transform ${color === col ? "ring-2 ring-white scale-110" : ""}`}
                        style={{ backgroundColor: col }} />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="save-cluster-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            Crea cluster
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
